import BulletList from '@tiptap/extension-bullet-list';
import CodeBlock from '@tiptap/extension-code-block';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

import './styles.css';

interface RichContentProps {
  content: string;
  onUpdate?: (content: string) => void;
}

const RichContent = ({ content, onUpdate }: RichContentProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      BulletList.configure({
        keepMarks: true,
        keepAttributes: false,
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: false,
      }),
      ListItem,
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'task-item',
        },
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      CodeBlock.configure({
        HTMLAttributes: {
          class: 'rounded-md bg-gray-800 p-4',
        },
      }),
    ],
    content,
    editable: !!onUpdate,
    onUpdate: onUpdate
      ? ({ editor }) => {
          onUpdate(editor.getHTML());
        }
      : undefined,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose max-w-none outline-none min-h-[200px] px-4 py-2',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.getHTML();
    if (currentContent !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor">
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichContent;
